'use client';

import { useState, useEffect, useRef } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  href: string;
}

interface TrustBadge {
  text: string;
}

interface Step {
  number: string;
  title: string;
  description: string;
  badge: string;
}

interface InfoCard {
  title: string;
  description: string;
}

interface ClaimCard {
  category: string;
  badge?: string;
  badgeStyle?: 'green' | 'dark';
  title: string;
  description: string;
  valueLabel: string;
  featured?: boolean;
  image?: string;
  imageAlt?: string;
}

interface Testimonial {
  initials: string;
  name: string;
  location: string;
  date: string;
  text: string;
  rating: number;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface AudienceCard {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────

const TALLY_URL = '/check';

const navItems: NavItem[] = [
{ label: 'Home', href: '#home' },
{ label: 'Why Claim Ontario?', href: '#claims' },
{ label: 'About', href: '#about' },
{ label: 'Contact', href: '#contact' }];


const trustBadges: TrustBadge[] = [
{ text: 'Homeowner Specialists' },
{ text: 'Fire & Smoke Claims' },
{ text: 'Panel of Experts' },
{ text: 'No Cure No Pay' },
{ text: '500+ Cases Handled' }];


const steps: Step[] = [
{
  number: '01',
  title: 'Free Claim Assessment',
  description:
  'Complete our free assessment in 5 minutes. We analyse your situation and determine whether you may have a valid claim — no obligations, no cost.',
  badge: 'Completely free and non-binding'
},
{
  number: '02',
  title: 'Expert Consultation',
  description:
  'A member of our expert panel contacts you for a personal consultation. We explain your rights, the strength of your claim, and what you may be entitled to — clearly and without obligation.',
  badge: 'Response within 24 hours'
},
{
  number: '03',
  title: 'We Handle Everything',
  description:
  'Full case management from documentation to resolution. We communicate with all parties, build your file, and pursue the maximum compensation you deserve.',
  badge: 'No Cure No Pay'
}];


const infoCards: InfoCard[] = [
{
  title: 'Expert Support for Homeowners Following Fire Damage',
  description:
  'We specialise in helping homeowners who have suffered significant losses following a fire make sure their claim reflects the full extent of their losses — including fire and smoke damage, structural damage, contents loss, and related costs that insurers often overlook.'
},
{
  title: 'Transparent, Success-Based Fees',
  description:
  "Our service is entirely based on 'No Cure, No Pay'. If your claim is unsuccessful, our services are completely free. If compensation is awarded, we charge a commission on the amount received."
},
{
  title: 'Experts on Your Side',
  description:
  'The insurance company has professionals working on its side. You should have experts working on yours. Our panel brings together experienced professionals who know what to look for and how to properly assess your losses — and we can challenge unfair denials and inadequate settlement offers.'
}];


const claimCards: ClaimCard[] = [
{
  category: 'Fire Damage',
  badge: 'Current Focus',
  badgeStyle: 'green',
  title: 'Property Damage from Fire',
  description:
  'Has your property been damaged or destroyed by fire in Ontario? You may be entitled to more than your insurer has offered. We assess your full entitlement — including fire damage, smoke contamination, structural loss, and contents — to make sure nothing is overlooked.',
  valueLabel: 'Full Assessment',
  featured: true,
  image: "https://images.unsplash.com/photo-1532272977223-ecfb7a459e62",
  imageAlt:
  'Aerial view of fire damage in Ontario landscape with smoke and burnt structures'
},
{
  category: 'Business Interruption',
  badge: 'High Priority',
  badgeStyle: 'green',
  title: 'Business Loss & Interruption Claims',
  description:
  'Forced evacuation, supply chain disruption, or loss of revenue due to fire? Businesses may have grounds for substantial compensation claims. We quantify every category of loss and challenge insurer undervaluations.',
  valueLabel: 'Tailored Assessment',
  featured: true,
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b6d78868-1779998684405.png',
  imageAlt:
  'Professional expert reviewing documents in a modern office setting for business claims'
},
{
  category: 'Insurance Disputes',
  title: 'Underpaid or Denied Insurance Claims',
  description:
  'Insurance companies sometimes undervalue or deny legitimate claims. We challenge unfair settlements and fight for the full compensation you are entitled to — including smoke damage, hidden structural damage, and contents insurers overlook.',
  valueLabel: 'Claim Review'
},
{
  category: 'Personal Loss',
  title: 'Personal Property & Asset Loss',
  description:
  'Lost personal belongings, vehicles, or assets due to fire? We help individuals document and pursue compensation for all categories of loss, including items that were not photographed or itemised before the fire.',
  valueLabel: 'Full Documentation'
},
{
  category: 'Evacuation Costs',
  title: 'Evacuation & Displacement Costs',
  description:
  'Emergency evacuation, temporary accommodation, and relocation costs may all be recoverable. We ensure every eligible expense is included in your claim — including costs that are often missed in standard insurance settlements.',
  valueLabel: 'All Costs Covered'
},
{
  category: 'Agricultural & Land',
  badge: 'Specialist',
  badgeStyle: 'dark',
  title: 'Agricultural & Land Damage',
  description:
  'Farmers and landowners face unique challenges after fire. We have specialist expertise in agricultural loss claims and long-term land damage assessment, including crop loss, livestock, and infrastructure.',
  valueLabel: 'Specialist Assessment'
}];


const testimonials: Testimonial[] = [
{
  initials: 'MR',
  name: 'Michael Robertson',
  location: 'Toronto, Ontario',
  date: 'March 2025',
  text: 'After the fire destroyed our family home, we had no idea where to start. The team guided us through every step and secured compensation we never knew we were entitled to — including smoke damage the insurer had completely ignored. Truly exceptional expertise.',
  rating: 5
},
{
  initials: 'SC',
  name: 'Sarah Chen',
  location: 'Mississauga, Ontario',
  date: 'January 2025',
  text: 'Our insurance company initially offered a settlement far below our actual losses. Claim Ontario challenged it and we received more than three times the original offer. Professional, knowledgeable, and genuinely on our side.',
  rating: 5
},
{
  initials: 'JT',
  name: 'James Thornton',
  location: 'Ontario',
  date: 'February 2025',
  text: 'As a business owner, the fire caused devastating losses. I was overwhelmed by the complexity of the claim. The expert panel handled everything with precision and we received full compensation for our business interruption.',
  rating: 5
},
{
  initials: 'LP',
  name: 'Linda Patel',
  location: 'GTA, Ontario',
  date: 'April 2025',
  text: 'I was sceptical at first, but the No Cure No Pay model meant I had nothing to lose. The assessment was thorough, the advice was clear, and the outcome exceeded my expectations. Highly recommended.',
  rating: 5
}];


const faqItems: FaqItem[] = [
{
  question: 'What does the free claim assessment involve?',
  answer:
  'The free assessment analyses your situation: the nature of your loss, the circumstances, your location, and any existing insurance or prior claims. Based on this, we provide a clear overview of whether you may have a valid claim and what you could potentially be entitled to.'
},
{
  question: 'How much compensation could I be entitled to?',
  answer:
  'This varies significantly depending on the nature and extent of your loss. Residential property damage claims can range from tens of thousands to hundreds of thousands of dollars. Business interruption and agricultural claims can be even larger. We determine your specific entitlement after the free assessment.'
},
{
  question: 'What does No Cure No Pay mean exactly?',
  answer:
  "You only pay when we successfully secure compensation for you. Our fee is a percentage of the compensation received. No result? No cost. This means our interests are fully aligned with yours — we only succeed when you succeed."
},
{
  question: 'How long does the claims process take?',
  answer:
  'After your free assessment, an expert from our panel contacts you within 24 hours. The claims process varies depending on complexity — straightforward cases may resolve in weeks, while complex cases can take several months. We manage all communication and keep you informed throughout.'
},
{
  question: 'Can I still make a claim if time has passed since the fire?',
  answer:
  'In many cases, yes. Limitation periods vary depending on the type of claim and jurisdiction. It is important to act promptly, but do not assume it is too late without speaking to us first. Complete the free assessment and we will advise you on your specific situation.'
},
{
  question: 'Do you work with individuals and businesses?',
  answer:
  'Yes. We assist homeowners, private individuals, families, business owners, farmers, and landowners. Each type of claim has specific considerations, and our panel of experts has experience across all categories of loss — from residential property claims to complex commercial disputes.'
},
{
  question: 'Are you a government agency or independent expert?',
  answer:
  'We are an independent panel of claims experts. We are not affiliated with any government body, insurance company, or claims management agency. We work exclusively for our clients to make sure they receive the full compensation they deserve.'
},
{
  question: 'What documentation do I need to start?',
  answer:
  'You do not need to have everything ready to start. Begin with the free assessment and we will guide you on exactly what documentation is needed for your specific claim. We help you gather and organise all required evidence.'
}];


const audienceCards: AudienceCard[] = [
{
  title: 'Homeowners',
  description:
  'We guide homeowners through the full claims process and pursue the maximum compensation for property damage, contents loss, and displacement costs.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_18f38115e-1770916997850.png',
  imageAlt:
  'Professional consultation between a lawyer and homeowner reviewing property damage documentation'
},
{
  title: 'Business Owners',
  description:
  'From small businesses to large enterprises, we have experience with complex business interruption and asset loss claims across all sectors.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10052f051-1772126103871.png',
  imageAlt:
  'Business owner and legal expert reviewing financial documents and claim files in professional office'
},
{
  title: 'Farmers & Landowners',
  description:
  'Agricultural and land damage claims require specialist knowledge. Our panel of experts understands the unique complexities of rural and agricultural losses.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16e8533bb-1786417641659.png",
  imageAlt:
  'Ontario agricultural landscape showing farmland and rural property affected by environmental damage'
},
{
  title: 'Investors & Landlords',
  description:
  'Investment properties and rental portfolios require careful handling. We manage all aspects of the claim, including coordination with tenants and insurers.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19f682179-1772165798830.png',
  imageAlt:
  'Modern commercial building representing investment property and real estate portfolio management'
}];


// ─── Sub-components ───────────────────────────────────────────────────────────

function CheckIcon({ size = 10 }: {size?: number;}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round">
      
      <polyline points="20 6 9 17 4 12" />
    </svg>);

}

function ArrowIcon({ size = 16, className = '' }: {size?: number;className?: string;}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      
      <path d="M7 17L17 7M17 7H7M17 7v10" />
    </svg>);

}

function StarIcon({ filled = true }: {filled?: boolean;}) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? '#2D7A4F' : 'none'} stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>);

}

function GreenDot({ small = false }: {small?: boolean;}) {
  const size = small ? 'w-4 h-4' : 'w-5 h-5';
  const iconSize = small ? 8 : 10;
  return (
    <div className={`${size} rounded-full bg-[#2D7A4F] flex items-center justify-center shrink-0`}>
      <CheckIcon size={iconSize} />
    </div>);

}

function SectionLabel({ text }: {text: string;}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-px bg-[#2D7A4F]" />
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2D7A4F]">
        {text}
      </span>
    </div>);

}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const claimRefs = useRef<(HTMLDivElement | null)[]>([]);
  const audienceRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            const id = entry.target.dataset.animId;
            if (id) {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0)';
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const allRefs = [...stepRefs.current, ...claimRefs.current, ...audienceRefs.current];
    allRefs.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (href: string) => {
    setMobileMenuOpen(false);
    if (href === '#home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="font-sans text-[#0F1B2D] bg-white overflow-x-hidden">

      {/* ── HEADER ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ?
        'bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#E5E0D8]' :
        'bg-white'}`
        }
        style={{ height: '64px' }}>
        
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between gap-3">
          {/* Logo */}
          <button
            onClick={() => scrollTo('#home')}
            className="flex items-center gap-2 leading-none shrink-0"
            aria-label="Claim Ontario — home">
            
            <div className="flex flex-col leading-none">
              <span className="text-base sm:text-lg font-bold text-[#0F1B2D] tracking-tight">
                Claim <span className="text-[#2D7A4F]">Ontario</span>
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
            <button
              key={item.label}
              onClick={() => scrollTo(item.href)}
              className="text-sm font-medium text-[#374151] hover:text-[#0F1B2D] px-4 py-2 rounded-[10px] hover:bg-[#F4F8F5] transition-all duration-150">
              
                {item.label}
              </button>
            )}
          </nav>

          {/* Header CTA — desktop */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={TALLY_URL}
              className="flex items-center gap-2 bg-[#2D7A4F] text-white text-sm font-semibold px-5 py-2.5 rounded-[10px] hover:bg-[#1f5537] transition-all duration-200 shadow-sm">
              
              Understand Your Rights
              <ArrowIcon size={14} />
            </a>
          </div>

          {/* Mobile right side: CTA + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href={TALLY_URL}
              className="flex items-center gap-1.5 bg-[#2D7A4F] text-white text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap">
              
              Quick Check
              <ArrowIcon size={12} />
            </a>
            <button
              className="p-2 rounded-lg text-[#374151] hover:bg-[#F4F8F5] shrink-0"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu">
              
              {mobileMenuOpen ?
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg> :

              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12h18M3 6h18M3 18h18" />
                </svg>
              }
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen &&
        <div className="md:hidden bg-white border-t border-[#E5E0D8] px-4 py-3 flex flex-col gap-1 shadow-lg">
            {navItems.map((item) =>
          <button
            key={item.label}
            onClick={() => scrollTo(item.href)}
            className="text-sm font-medium text-[#374151] text-left px-3 py-3 rounded-lg hover:bg-[#F4F8F5] transition-colors">
            
                {item.label}
              </button>
          )}
            <a
            href={TALLY_URL}
            onClick={() => setMobileMenuOpen(false)}
            className="mt-2 flex items-center justify-center gap-2 bg-[#2D7A4F] text-white text-sm font-semibold px-5 py-3.5 rounded-[10px]">
            
              Understand Your Rights
              <ArrowIcon size={14} />
            </a>
          </div>
        }
      </header>

      {/* ── HERO ── */}
      <section
        id="home"
        className="relative overflow-hidden bg-[#0F1B2D]"
        style={{ paddingTop: '64px', minHeight: 'calc(100svh - 0px)' }}>
        
        {/* Background image */}
        <div className="absolute inset-0" style={{ top: 0, height: '100%' }}>
          <img
            src="https://images.unsplash.com/photo-1665212948695-74039ed63aab"
            alt="Fire damage landscape with smoke rising over forested mountains"
            className="w-full h-full object-cover object-center" />
          
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1B2D]/92 via-[#0F1B2D]/65 to-[#0F1B2D]/35" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B2D]/85 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="flex flex-col justify-center py-10 sm:py-16 lg:py-24 min-h-[calc(100svh-64px)]">
              {/* Label */}
              <div
                className="inline-flex items-center gap-2 mb-4 sm:mb-6 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.05s', animationFillMode: 'forwards' }}>
                
                <div className="w-6 sm:w-8 h-px bg-[#2D7A4F] shrink-0" />
                <span className="text-[9px] sm:text-xs font-semibold uppercase tracking-[0.1em] sm:tracking-[0.25em] text-black bg-white/50 px-2 sm:px-3 py-1 rounded-sm leading-snug">
                  A panel of local experts in your area - no cure no pay
                </span>
              </div>

              {/* Heading */}
              <h1
                className="text-[1.6rem] xs:text-[1.75rem] sm:text-[2.6rem] lg:text-[3.8rem] font-bold text-white leading-[1.1] tracking-tight mb-4 sm:mb-6 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.15s', animationFillMode: 'forwards' }}>
                
                After a fire, are you getting{' '}
                <span className="text-white">what you actually deserve?</span>
              </h1>

              {/* Subtext */}
              <p
                className="text-sm sm:text-base lg:text-lg text-white leading-relaxed mb-5 sm:mb-6 max-w-lg opacity-0 animate-fade-in"
                style={{ animationDelay: '0.25s', animationFillMode: 'forwards' }}>
                
                Insurance companies have experts on their side. Make sure you have experts on yours. Many people in Ontario affected by fire damage receive far less than they are entitled to — or nothing at all. Find out where your rights stand.
              </p>

              {/* Trust messages */}
              <div
                className="flex flex-col gap-3 mb-6 sm:mb-8 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
                <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 max-w-lg">
                  <div className="w-7 h-7 rounded-full bg-[#2D7A4F] flex items-center justify-center shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="text-sm font-bold text-white leading-snug">We Can Overturn Denials</p>
                </div>
                <div className="inline-flex items-start gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 max-w-lg">
                  <div className="w-7 h-7 rounded-full bg-[#2D7A4F] flex items-center justify-center shrink-0 mt-0.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="text-sm text-white/90 leading-snug font-bold">Our panel can usually obtain a significantly higher amount than the insurance company's settlement offers.</p>
                </div>
              </div>

              {/* David Hartley card — mobile only, shown in hero */}
              <div
                className="lg:hidden mb-5 sm:mb-6 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.38s', animationFillMode: 'forwards' }}>
                
                <div className="flex items-center gap-3 p-3.5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 max-w-xs">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#2D7A4F] shrink-0">
                    <img
                      src="/assets/images/expert_portrait_david_hartley.png"
                      alt="David Hartley — Certified International Claims Lawyer"
                      className="w-full h-full object-cover object-top" />
                    
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">Claim Ontario</p>
                    <p className="text-[10px] font-medium text-white">A Panel of Experts in Your Area</p>
                    <p className="text-[10px] text-white/60">500+ cases · No Cure No Pay · We are on your side</p>
                  </div>
                </div>
              </div>

              {/* Bullet points */}
              <ul
                className="flex flex-col gap-2.5 sm:gap-3 mb-7 sm:mb-10 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.35s', animationFillMode: 'forwards' }}>
                
                {[
                'Answer 5 quick questions to find out where your rights stand',
                'Expert support for homeowners following fire, smoke and structural damage',
                'No compensation secured? You pay nothing|bold'].
                map((item) => {
                  const isBold = item.endsWith('|bold');
                  const text = isBold ? item.replace('|bold', '') : item;
                  return (
                    <li key={text} className="flex items-start gap-3">
                      <GreenDot />
                      <span
                        className={`text-sm leading-snug ${
                        isBold ? 'font-bold text-white' : 'font-medium text-white'}`
                        }>
                        
                        {text}
                      </span>
                    </li>);

                })}
              </ul>

              {/* CTA buttons */}
              <div
                className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.45s', animationFillMode: 'forwards' }}>
                
                <a
                  href={TALLY_URL}
                  className="group flex items-center justify-center gap-2.5 bg-[#2D7A4F] text-white px-6 sm:px-7 py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-[#1f5537] transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-auto active:scale-[0.98]">
                  
                  Understand Your Rights
                  <ArrowIcon
                    size={16}
                    className="group-hover:translate-x-1 transition-transform shrink-0" />
                  
                </a>
                <button
                  onClick={() => scrollTo('#contact')}
                  className="flex items-center justify-center gap-2.5 border border-white/30 bg-white/10 backdrop-blur-sm text-white px-6 py-4 rounded-lg text-sm sm:text-base font-medium hover:bg-white/20 hover:border-white/50 transition-all duration-200 w-full sm:w-auto">
                  
                  Book a Consultation
                </button>
              </div>

              {/* Trust badges */}
              <div
                className="mt-6 sm:mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-4 opacity-0 animate-fade-in"
                style={{ animationDelay: '0.55s', animationFillMode: 'forwards' }}>
                
                <div className="flex items-center gap-2">
                  <GreenDot />
                  <span className="text-xs sm:text-sm font-semibold text-white/90">No Cure No Pay</span>
                </div>
                <div className="hidden sm:block w-px h-4 bg-white/20" />
                <div className="flex items-center gap-2">
                  <GreenDot />
                  <span className="text-xs sm:text-sm font-semibold text-white/90">Panel of Experts</span>
                </div>
                <div className="hidden sm:block w-px h-4 bg-white/20" />
                <div className="flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 100 100" fill="#8B3A3A" xmlns="http://www.w3.org/2000/svg" aria-label="Ontario" role="img">
                    <path d="M50 5 L55 30 L75 20 L65 40 L90 38 L72 52 L80 75 L58 65 L55 95 L50 80 L45 95 L42 65 L20 75 L28 52 L10 38 L35 40 L25 20 L45 30 Z" />
                  </svg>
                  <span className="text-xs sm:text-sm font-semibold text-white/90">Ontario Focus</span>
                </div>
              </div>
            </div>

            {/* David Hartley profile card — desktop right column */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="flex items-center gap-5 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 max-w-sm w-full">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#2D7A4F] shrink-0">
                  <img
                    src="/assets/images/expert_portrait_david_hartley.png"
                    alt="David Hartley — Certified International Claims Lawyer"
                    className="w-full h-full object-cover object-top" />
                  
                </div>
                <div>
                  <p className="text-base font-bold text-white">Claim Ontario</p>
                  <p className="text-xs font-medium text-white mb-1">
                    A Panel of Experts in Your Area
                  </p>
                  <p className="text-xs text-white/60 leading-snug">
                    500+ cases · No Cure No Pay · We are on your side
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </section>

      {/* ── TRUST BAR ── */}
      <div className="bg-[#F4F8F5] border-b border-[#D6E8DC]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-center gap-x-3 sm:gap-x-8 gap-y-2">
            {trustBadges.map((badge, i) =>
            <div key={badge.text} className="flex items-center gap-1.5 sm:gap-2">
                {i > 0 && <div className="hidden sm:block w-px h-4 bg-[#E5E0D8]" />}
                <GreenDot small />
                <span className="text-xs font-medium text-[#374151]">{badge.text}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── ONTARIO COVERAGE BANNER ── */}
      <div className="bg-[#0F1B2D] border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-3 sm:py-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 text-center">
            <svg width="14" height="14" viewBox="0 0 100 100" fill="#2D7A4F" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M50 5 L55 30 L75 20 L65 40 L90 38 L72 52 L80 75 L58 65 L55 95 L50 80 L45 95 L42 65 L20 75 L28 52 L10 38 L35 40 L25 20 L45 30 Z" />
            </svg>
            <span className="text-xs sm:text-sm font-semibold text-white">
              We cover all of Ontario, including the entire GTA region.
            </span>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section id="approach" className="py-14 sm:py-20 lg:py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="mb-10 sm:mb-16 max-w-2xl">
            <SectionLabel text="Our Approach" />
            <h2 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-bold text-[#0F1B2D] mb-3 sm:mb-4 leading-tight">
              Need help with a complex claim?{' '}
              <span className="text-[#2D7A4F]">We handle it.</span>
            </h2>
            <p className="text-base sm:text-lg text-[#6B7280] leading-relaxed">
              We guide your claim from initial assessment to final resolution — with expert legal
              knowledge and personal attention at every step.
            </p>
          </div>

          {/* Step cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-10 sm:mb-14">
            {steps.map((step, i) =>
            <div
              key={step.number}
              ref={(el) => {
                stepRefs.current[i] = el;
              }}
              data-anim-id={`step-${i}`}
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`
              }}>
              
                <div className="bg-white border border-[#D6E8DC] rounded-xl p-4 sm:p-5 h-full flex flex-col hover:border-[#2D7A4F] hover:shadow-card-hover transition-all duration-300 group">
                  <h3 className="text-sm sm:text-base font-semibold text-[#0F1B2D] mb-2 group-hover:text-[#2D7A4F] transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#6B7280] leading-relaxed flex-1">{step.description}</p>
                  <div className="mt-4 flex items-center gap-2 pt-3 border-t border-[#E5E0D8]">
                    <GreenDot small />
                    <span className="text-xs font-semibold text-[#374151]">{step.badge}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-14">
            {infoCards.map((card) =>
            <div key={card.title} className="bg-[#F4F8F5] rounded-xl p-5 sm:p-6 border border-[#D6E8DC]">
                <h3 className="text-sm sm:text-base font-semibold text-[#0F1B2D] mb-2 sm:mb-3">
                  {card.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{card.description}</p>
              </div>
            )}
          </div>

          <div className="text-center">
            <a
              href={TALLY_URL}
              className="group inline-flex items-center gap-2.5 bg-[#2D7A4F] text-white px-6 sm:px-8 py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-[#1f5537] transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 w-full sm:w-auto justify-center">
              
              Understand Your Rights
              <ArrowIcon size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* ── WHY CLAIM ONTARIO? ── */}
      <section id="claims" className="py-14 sm:py-20 lg:py-28 bg-[#0F1B2D] relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#1a2d45] opacity-50 blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#2D7A4F] opacity-5 blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12 text-center">
            <SectionLabel text="Why Claim Ontario?" />
            <h2 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-bold text-white mt-3 leading-tight">
              Helping homeowners make sure their fire claim reflects the full extent of their losses
            </h2>
            <p className="text-sm sm:text-base text-white/60 mt-3 max-w-xl mx-auto">
              Including fire and smoke damage, structural damage, contents loss, and related property claims.
            </p>
          </div>

          {/* Audience cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
            {audienceCards.map((card, i) =>
            <a
              key={card.title}
              href={TALLY_URL}
              ref={(el) => {
                audienceRefs.current[i] = el as HTMLDivElement | null;
              }}
              data-anim-id={`audience-${i}`}
              className="group relative rounded-xl overflow-hidden cursor-pointer"
              style={{
                opacity: 0,
                transform: 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s`
              }}>
              
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img
                  src={card.image}
                  alt={card.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B2D]/80 via-[#0F1B2D]/20 to-transparent" />
                  {card.title === 'Homeowners' &&
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex items-center gap-1 bg-[#2D7A4F] text-white text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-md">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      Our Expertise
                    </div>
                }
                  <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4">
                    <h3 className="text-[11px] sm:text-sm font-semibold text-white mb-0.5 sm:mb-1">
                      {card.title}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-white/60 leading-snug line-clamp-2 hidden sm:block">
                      {card.description}
                    </p>
                  </div>
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowIcon size={14} className="text-white" />
                  </div>
                </div>
              </a>
            )}
          </div>

          {/* CTA */}
          <div className="flex justify-center">
            <a
              href={TALLY_URL}
              className="group inline-flex items-center justify-center gap-2.5 bg-[#2D7A4F] text-white px-6 sm:px-8 py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-[#1f5537] transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-auto">
              
              Understand Your Rights
              <ArrowIcon size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="reviews" className="py-14 sm:py-20 lg:py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12">
            <SectionLabel text="Client Reviews" />
            <h2 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-bold text-[#0F1B2D] mb-2 leading-tight">
              What our clients say
            </h2>
            <p className="text-[#6B7280]">Read what others say about working with us</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
            {/* Featured testimonial */}
            <div>
              <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-[#2D7A4F] shrink-0 bg-[#F4F8F5] flex items-center justify-center">
                  <span className="text-xs sm:text-sm font-bold text-[#0F1B2D]">
                    {testimonials[activeTestimonial]?.initials}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0F1B2D]">
                    {testimonials[activeTestimonial]?.name}
                  </p>
                  <p className="text-xs text-[#6B7280]">
                    Panel of Experts — Claim Ontario
                  </p>
                </div>
              </div>

              <div className="bg-white border border-[#E5E0D8] rounded-xl p-5 sm:p-8 shadow-card">
                <div className="flex gap-1 mb-3 sm:mb-4">
                  {Array.from({ length: 5 }).map((_, i) =>
                  <StarIcon key={i} filled />
                  )}
                </div>
                <p className="text-sm sm:text-base text-[#0F1B2D] leading-relaxed italic mb-5 sm:mb-6">
                  &ldquo;{testimonials[activeTestimonial]?.text}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-4 sm:pt-5 border-t border-[#E5E0D8]">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#F8F6F2] border border-[#E5E0D8] flex items-center justify-center text-xs sm:text-sm font-semibold text-[#0F1B2D] shrink-0">
                    {testimonials[activeTestimonial]?.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F1B2D]">
                      {testimonials[activeTestimonial]?.name}
                    </p>
                    <p className="text-xs text-[#6B7280]">
                      {testimonials[activeTestimonial]?.location} ·{' '}
                      {testimonials[activeTestimonial]?.date}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dots */}
              <div className="flex gap-2 mt-4">
                {testimonials.map((_, i) =>
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Review ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeTestimonial ? 'w-6 bg-[#2D7A4F]' : 'w-1.5 bg-[#D6E8DC]'}`
                  } />

                )}
              </div>
            </div>

            {/* Side testimonials */}
            <div className="flex flex-col gap-3 sm:gap-4">
              {testimonials.slice(1, 4).map((t, i) =>
              <div
                key={t.name}
                onClick={() => setActiveTestimonial(i + 1)}
                className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl border border-[#E5E0D8] cursor-pointer hover:border-[#2D7A4F] hover:shadow-card transition-all duration-200 group">
                
                  <div className="w-9 h-9 rounded-full bg-[#F4F8F5] border border-[#D6E8DC] flex items-center justify-center text-xs font-semibold text-[#0F1B2D] shrink-0">
                    {t.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <p className="text-sm font-semibold text-[#0F1B2D] truncate">{t.name}</p>
                      <div className="flex gap-0.5 shrink-0">
                        {Array.from({ length: 5 }).map((_, si) =>
                      <StarIcon key={si} filled />
                      )}
                      </div>
                    </div>
                    <p className="text-xs text-[#6B7280] line-clamp-2 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              )}

              {/* CTA card */}
              <div className="bg-[#F4F8F5] rounded-xl p-5 sm:p-6 border border-[#D6E8DC]">
                <p className="text-sm font-semibold text-[#0F1B2D] mb-1">We are on your side</p>
                <p className="text-xs text-[#6B7280] mb-4">
                  The insurance side has professionals working for them. Make sure you have experts working for you.
                </p>
                <a
                  href={TALLY_URL}
                  className="group flex items-center gap-2 bg-[#2D7A4F] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#1f5537] transition-all duration-200 w-full justify-center sm:justify-start">
                  
                  Understand Your Rights
                  <ArrowIcon size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-14 sm:py-20 lg:py-28 bg-[#F8F6F2]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20">
            {/* Left */}
            <div>
              <SectionLabel text="Frequently Asked Questions" />
              <h2 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-bold text-[#0F1B2D] mb-3 sm:mb-5 leading-tight">
                Common questions
              </h2>
              <p className="text-base sm:text-lg text-[#6B7280] leading-relaxed mb-6 sm:mb-8">
                Have a different question? A member of our expert panel is available to speak with you personally.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={TALLY_URL}
                  className="group flex items-center justify-center gap-2 bg-[#2D7A4F] text-white text-sm font-semibold px-6 py-3.5 rounded-lg hover:bg-[#1f5537] transition-all duration-200 w-full sm:w-auto">
                  
                  Understand Your Rights
                  <ArrowIcon size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="tel:647-642-0960"
                  className="flex items-center justify-center gap-2 border border-[#D6E8DC] text-sm font-medium text-[#374151] px-6 py-3.5 rounded-lg hover:border-[#2D7A4F] hover:text-[#2D7A4F] transition-all duration-200 w-full sm:w-auto">
                  
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2D7A4F] shrink-0">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                  </svg>
                  Call 647-642-0960
                </a>
              </div>

              {/* No Cure No Pay badge */}
              <div className="mt-6 sm:mt-8 flex items-center gap-4 p-4 bg-white rounded-xl border border-[#D6E8DC]">
                <div className="w-10 h-10 rounded-full bg-[#2D7A4F] flex items-center justify-center shrink-0">
                  <CheckIcon size={12} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0F1B2D]">No Cure No Pay</p>
                  <p className="text-xs text-[#6B7280]">You only pay when we secure compensation</p>
                </div>
              </div>
            </div>

            {/* Right — FAQ accordion */}
            <div className="flex flex-col gap-2 sm:gap-3">
              {faqItems.map((item, i) =>
              <div key={i} className="bg-white rounded-xl border border-[#E5E0D8] overflow-hidden">
                  <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-3 sm:gap-4 px-4 sm:px-6 py-4 text-left">
                  
                    <span className="text-sm font-semibold text-[#0F1B2D] leading-snug">
                      {item.question}
                    </span>
                    <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`shrink-0 text-[#6B7280] transition-transform duration-200 ${
                    openFaq === i ? 'rotate-180' : ''}`
                    }>
                    
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  {openFaq === i &&
                <div className="px-4 sm:px-6 pb-4 sm:pb-5">
                      <p className="text-sm text-[#6B7280] leading-relaxed">{item.answer}</p>
                    </div>
                }
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT / LEAD GEN ── */}
      <section id="contact" className="py-14 sm:py-20 lg:py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <SectionLabel text="Free Claim Assessment" />
            <h2 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-bold text-[#0F1B2D] mb-3 sm:mb-4 leading-tight">
              Could you be entitled to compensation?
            </h2>
            <p className="text-base sm:text-lg text-[#6B7280] leading-relaxed mb-6 sm:mb-8">
              Answer a few quick questions to see whether your situation may be worth discussing with an expert — completely free and non-binding.
            </p>

            {/* Trust points */}
            <div className="flex flex-col gap-4 mb-6 sm:mb-8">
              {[
              {
                title: 'Expert Support for Homeowners Following Fire Damage',
                desc: 'Whether you are dealing with a residential property claim, fire or smoke damage, or a complex insurance dispute — we have the expertise to help you get what you deserve.'
              },
              {
                title: 'No Cure No Pay',
                desc: 'You only pay if we successfully secure compensation for you. No result, no cost.'
              },
              {
                title: 'Confidential & Secure',
                desc: 'Your information is treated with complete confidentiality and professional discretion.'
              }].
              map((point) =>
              <div key={point.title} className="flex gap-3 sm:gap-4">
                  <GreenDot />
                  <div>
                    <p className="text-sm font-semibold text-[#0F1B2D]">{point.title}</p>
                    <p className="text-sm text-[#6B7280]">{point.desc}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Book consultation CTA */}
            <div className="mb-5 sm:mb-6">
              <a
                href={TALLY_URL}
                className="group flex items-center justify-center gap-2.5 bg-[#2D7A4F] text-white text-sm font-semibold px-6 py-4 rounded-lg hover:bg-[#1f5537] transition-all duration-200 shadow-sm w-full sm:w-auto">
                
                Start the Quick Check
                <ArrowIcon size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Contact info */}
            <div className="p-4 sm:p-5 bg-[#F4F8F5] rounded-xl border border-[#D6E8DC]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2D7A4F] mb-3">
                Direct Contact
              </p>
              <div className="flex flex-col gap-2.5">
                <a
                  href="tel:+16476420960"
                  className="flex items-center gap-2.5 text-sm font-medium text-[#374151] hover:text-[#2D7A4F] transition-colors">
                  
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2D7A4F] shrink-0">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                  </svg>
                  +1 647 642 0960
                </a>
                <a
                  href="mailto:info@claimontario.com"
                  className="flex items-center gap-2.5 text-sm font-medium text-[#374151] hover:text-[#2D7A4F] transition-colors">
                  
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2D7A4F] shrink-0">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  info@claimontario.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0F1B2D] text-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
            {/* Brand */}
            <div className="flex flex-col gap-4 sm:gap-5">
              <div className="flex items-center gap-2">
                <div>
                  <span className="text-xl font-bold tracking-tight">
                    Claim <span className="text-[#2D7A4F]">Ontario</span>
                  </span>
                  <span className="block text-[10px] font-medium text-white/40 tracking-[0.2em] uppercase mt-0.5">
                    ClaimOntario.com
                  </span>
                </div>
              </div>
              <p className="text-sm text-white/50 leading-relaxed max-w-xs">
                Expert support for homeowners and businesses following fire damage.
                <br />
                No Cure No Pay · Panel of Experts · 500+ Cases
              </p>
              <div className="flex items-center gap-3 mt-1">
                <a
                  href="https://wa.me/16476420960"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#2D7A4F] hover:text-white transition-all"
                  aria-label="WhatsApp">
                  
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>
                <a
                  href="mailto:info@claimontario.com"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#2D7A4F] hover:text-white transition-all"
                  aria-label="Email">
                  
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-4 sm:mb-5">
                Quick Links
              </h3>
              <ul className="flex flex-col gap-2 sm:gap-2.5">
                {[
                { label: 'Home', href: '#home' },
                { label: 'Our Approach', href: '#approach' },
                { label: 'Why Claim Ontario?', href: '#claims' },
                { label: 'Reviews', href: '#reviews' },
                { label: 'FAQ', href: '#faq' },
                { label: 'Privacy Policy', href: '#' },
                { label: 'Terms & Conditions', href: '#' }].
                map((link) =>
                <li key={link.label}>
                    <button
                    onClick={() => scrollTo(link.href)}
                    className="text-sm text-white/50 hover:text-[#2D7A4F] transition-colors text-left">
                    
                      {link.label}
                    </button>
                  </li>
                )}
              </ul>
            </div>

            {/* Contact */}
            <div className="sm:col-span-2 md:col-span-1">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-4 sm:mb-5">
                Contact
              </h3>
              <p className="text-sm text-white/50 mb-4 sm:mb-5">
                Call or email our Managing Partner for a free consultation.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:+16476420960"
                  className="flex items-center gap-2.5 text-sm font-medium text-white/70 hover:text-[#2D7A4F] transition-colors">
                  
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2D7A4F] shrink-0">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                  </svg>
                  +1 647 642 0960
                </a>
                <a
                  href="mailto:info@claimontario.com"
                  className="flex items-center gap-2.5 text-sm font-medium text-white/70 hover:text-[#2D7A4F] transition-colors">
                  
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2D7A4F] shrink-0">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  info@claimontario.com
                </a>
                <div className="flex items-center gap-2.5 text-sm text-white/50">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2D7A4F] shrink-0">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  100 Richmond St W, Toronto, Ontario
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 text-center sm:text-left">
            <p className="text-xs text-white/30">
              © 2025 Claim Ontario · Expert Support for Homeowners Following Fire Damage
            </p>
            <p className="text-xs text-white/30">
              Panel of Experts · No Cure No Pay · Confidential
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-white/30 hover:text-white/60 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-xs text-white/30 hover:text-white/60 transition-colors">
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>);

}